import React, {useState, useCallback, memo} from 'react';
import {
    Grid, Button, Typography, TextField, Stack, Box, Alert,
} from "@mui/material";
import {Link as LinkUI, useNavigate} from "react-router-dom";
import {useDispatch} from "react-redux";
import {handleRegister} from "../index";
import {styled} from "@mui/material/styles";

const Link = styled(LinkUI)(({ theme }) => ({
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontSize: '1.2rem',
    '&:hover': {
        cursor: 'pointer',
        color: theme.palette.primary.dark,
    },
}));

export const Register = memo(() => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [formState, setFormState] = useState({
        isLoading: false,
        message: "",
        status: "info",
        errors: {},
        step: 1,
        formData: {
            first_name: 'Danish',
            last_name: 'Ahmad',
            email: 'danish@havenfire.com',
            mobile: '23234234',
            company_name: 'Haven Fire & Safety',
        }
    });

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormState(prev => ({
            ...prev,
            errors: { ...prev.errors, [name]: null },
            formData: { ...prev.formData, [name]: value }
        }));
    }, []);

    const validateStep = useCallback(() => {
        const newErrors = {};
        if (formState.step === 1) {
            if (!formState.formData.first_name) newErrors.first_name = "First name is required";
            if (!formState.formData.email) newErrors.email = "Email is required";
            if (!formState.formData.mobile) newErrors.mobile = "Mobile is required";
        } else if (formState.step === 2) {
            if (!formState.formData.company_name) newErrors.company_name = "Company name is required";
        }
        setFormState(prev => ({ ...prev, errors: newErrors }));
        return Object.keys(newErrors).length === 0;
    }, [formState.step, formState.formData]);

    const handleNext = useCallback(() => {
        if (validateStep()) {
            setFormState(prev => ({ ...prev, step: prev.step + 1 }));
        }
    }, [validateStep]);

    const handleSubmit = useCallback(async (e) => {
        if (!validateStep()) {
            return;
        }

        setFormState(prev => ({ ...prev, isLoading: true }));

        const res = await handleRegister({ dispatch, data: formState.formData });
        
        setFormState(prev => ({ ...prev, isLoading: false }));

        if(res.success){
            setFormState(prev => ({
                ...prev,
                formData: {
                    first_name: '',
                    last_name: '',
                    email: '',
                    mobile: '',
                    company_name: '',
                }
            }));
            const subdomain = res.data?.ref;

            if (subdomain) {
                navigate('/register/verify');
            } else {
                navigate('/403');
            }
        }
        setFormState(prev => ({
            ...prev,
            errors: res.errors,
            message: res.message,
            status: res.status
        }));
    }, [validateStep, formState.formData, dispatch, navigate]);

    const handlePrev = useCallback(() => {
        setFormState(prev => ({ ...prev, step: prev.step - 1 }));
    }, []);

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Typography align={"center"} mb={4} fontWeight={"bold"} className={"title"} variant={"h1"}>Register</Typography>
            </Grid>
            <Grid item xs={12}>
                <Grid container spacing={2}>
                    {formState.message && (
                        <Grid item xs={12}>
                            <Alert severity={formState.status}>{formState.message}</Alert>
                        </Grid>
                    )}
                    {formState.step === 1 && (
                        <>
                            <Grid item xs={12}>
                                <TextField
                                    value={formState.formData.first_name}
                                    onChange={handleChange}
                                    required
                                    id="first_name"
                                    name="first_name"
                                    label="First name"
                                    fullWidth
                                    error={!!formState.errors.first_name}
                                    helperText={formState.errors.first_name}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    value={formState.formData.last_name}
                                    onChange={handleChange}
                                    id="last_name"
                                    name="last_name"
                                    label="Last name (optional)"
                                    fullWidth
                                    error={!!formState.errors.last_name}
                                    helperText={formState.errors.last_name}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    value={formState.formData.email}
                                    onChange={handleChange}
                                    id="email"
                                    name="email"
                                    label="Email"
                                    fullWidth
                                    error={!!formState.errors.email}
                                    helperText={formState.errors.email}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    value={formState.formData.mobile}
                                    onChange={handleChange}
                                    required
                                    id="mobile"
                                    name="mobile"
                                    label="Mobile"
                                    fullWidth
                                    error={!!formState.errors.mobile}
                                    helperText={formState.errors.mobile}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Stack justifyContent={"flex-end"} direction={"row"}>
                                    <Button
                                        fullWidth={true}
                                        variant="contained"
                                        onClick={handleNext}
                                        sx={{ height: 50, fontSize: 18, textTransform: "capitalize" }}
                                    >
                                        Next
                                    </Button>
                                </Stack>
                            </Grid>
                        </>
                    )}
                    {formState.step === 2 && (
                        <>
                            <Grid item xs={12}>
                                <TextField
                                    value={formState.formData.company_name}
                                    onChange={handleChange}
                                    required
                                    id="company_name"
                                    name="company_name"
                                    label="Company name"
                                    fullWidth
                                    error={!!formState.errors.company_name}
                                    helperText={formState.errors.company_name}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Stack justifyContent={"space-between"} direction={"row"}>
                                    <Button
                                        fullWidth={false}
                                        variant="outlined"
                                        onClick={handlePrev}
                                        sx={{ height: 50, fontSize: 18, textTransform: "capitalize", width: '48%' }}
                                    >
                                        Back
                                    </Button>
                                    <Button
                                        fullWidth={false}
                                        variant="contained"
                                        onClick={handleSubmit}
                                        disabled={formState.isLoading}
                                        sx={{ height: 50, fontSize: 18, textTransform: "capitalize", width: '48%' }}
                                    >
                                        {formState.isLoading ? 'Registering...' : 'Register'}
                                    </Button>
                                </Stack>
                            </Grid>
                        </>
                    )}
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <Box mt={3} sx={{ textAlign: "center" }}>
                    <Typography variant={"body1"}>Already have an account? <Link to="/login">Login</Link></Typography>
                </Box>
            </Grid>
        </Grid>
    );
});